import React, { useEffect, useMemo, useState } from 'react';
import { formatNumEU, formatPct, signedNum } from '../dummyData';
import { HiddenValue } from '../ValueHideContext';
import { useFilter, SortOption } from '../FilterContext';
import {
  Subscription,
  SubscriptionSummary,
} from '../../../../types/subscription';
import { SortingToggleOptions } from '../../types';
import { getBotColor } from '../../ClusterItem/Charts/helpers';
import { useDispatch } from 'react-redux';
import { setActiveSubscriptionId } from '../../../../store/slices/subscription';
import { useNavigate } from 'react-router-dom';
import BotItemChart from '../../ClusterItem/BotItem/Chart';

const sortOptionToToggle = (s: SortOption): SortingToggleOptions => {
  switch (s) {
    case 'Total Stable Profit':
      return SortingToggleOptions.TotalStableProfit;
    case 'Total Asset Profit':
      return SortingToggleOptions.TotalAssetProfit;
    case 'Current Stable Profit':
      return SortingToggleOptions.CurrentStableProfit;
    case 'Current Asset Profit':
      return SortingToggleOptions.CurrentAssetProfit;
    case 'Non-Algo Diff':
      return SortingToggleOptions.NoAlgorithmicDifference;
    case 'Total Value':
    default:
      return SortingToggleOptions.TotalValue;
  }
};

// ── Inline Asset/Quote bar ──────────────────────────────────────────────────

const QuoteAssetBar: React.FC<{ quoteRatio: number }> = ({ quoteRatio }) => {
  const qPct = Math.max(0, Math.min(100, quoteRatio * 100));
  const aPct = 100 - qPct;
  return (
    <div className="bot-list-aq-bar">
      {qPct > 0 && (
        <div className="bot-list-aq-quote" style={{ width: `${qPct}%` }} />
      )}
      {aPct > 0 && (
        <div className="bot-list-aq-asset" style={{ width: `${aPct}%` }} />
      )}
    </div>
  );
};

// ── BotListRow ────────────────────────────────────────────────────────────────

interface BotListRowProps {
  sub: SubscriptionSummary;
  delayIndex?: number;
}

const BotListRow: React.FC<BotListRowProps> = ({ sub, delayIndex = 0 }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sort } = useFilter();
  const activeToggleOption = useMemo(() => sortOptionToToggle(sort), [sort]);

  // Stagger chart render per row index — same approach as the grid BotItem.
  const [shouldRenderChart, setShouldRenderChart] = useState(false);
  useEffect(() => {
    setShouldRenderChart(false);
    const id = setTimeout(() => {
      setShouldRenderChart(true);
    }, 1000 + 300 * delayIndex);
    return () => clearTimeout(id);
  }, [delayIndex, activeToggleOption]);

  const totalStablePct = sub.profits?.total.stable.relative ?? 0;
  const totalStableAbs = sub.profits?.total.stable.absolute ?? 0;
  const totalAssetPct = sub.profits?.total.asset.relative ?? 0;
  const totalAssetAbs =
    (sub.profits?.total.asset.absolute ?? 0) * sub.latestPrice;
  const currentProfitPct = sub.currentProfit ?? 0;
  const noAlgoPct = sub.profits?.noAlgorithmicProgressionProfit.relative ?? 0;

  const totalStablePositive = totalStablePct >= 0;
  const totalAssetPositive = totalAssetPct >= 0;
  const currentProfitPositive = currentProfitPct >= 0;
  const noAlgoPositive = noAlgoPct >= 0;
  const dotColor = sub.isActive ? '#00B061' : '#F0162F';
  const color = getBotColor(sub.baseAsset);
  const quoteRatio =
    sub.totalBalanceInStable > 0
      ? sub.stableBalance / sub.totalBalanceInStable
      : 0;

  const handleRowClick = () => {
    dispatch(setActiveSubscriptionId(sub.id));
    navigate('/sub');
  };

  return (
    <div className="bot-list-row-wrapper">
      <div
        className="bot-list-row"
        onClick={handleRowClick}
        style={{ cursor: 'pointer' }}
      >
        {/* Icon */}
        <div className="bot-list-icon" style={{ background: color }}>
          <img
            src={`/resources/icons/${sub.baseAsset.toLowerCase()}.svg`}
            alt={sub.baseAsset}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          <span
            className="bot-list-icon-dot"
            style={{ background: dotColor }}
          />
        </div>

        {/* Pair + exchange */}
        <div className="bot-list-col">
          <span className="bot-list-pair-name">
            {sub.baseAsset}
            <span className="bot-list-pair-quote"> / {sub.quoteAsset}</span>
          </span>
          <span className="bot-list-pair-exch">{sub.exchange}</span>
        </div>

        {/* Real chart — same data series as the grid card */}
        <div
          className="bot-list-chart-cell"
          onClick={(e) => e.stopPropagation()}
          style={{ paddingRight: 24 }}
        >
          {shouldRenderChart && (
            <BotItemChart
              subscription={sub as unknown as Subscription}
              activeToggleOption={activeToggleOption}
            />
          )}
        </div>

        {/* Total value */}
        <span className="bot-list-cell">
          <HiddenValue width={55}>
            ${formatNumEU(sub.totalBalanceInStable)}
          </HiddenValue>
        </span>

        {/* Total stable */}
        <div className="bot-list-col">
          <span
            className="bot-list-cell"
            style={{ color: totalStablePositive ? '#00B061' : '#F0162F' }}
          >
            {formatPct(totalStablePct)}
          </span>
          <span
            className="bot-list-cell-secondary"
            style={{ color: totalStablePositive ? '#00B061' : '#F0162F' }}
          >
            <HiddenValue>{signedNum(totalStableAbs)}</HiddenValue>
          </span>
        </div>

        {/* Total asset */}
        <div className="bot-list-col">
          <span
            className="bot-list-cell"
            style={{ color: totalAssetPositive ? '#00B061' : '#F0162F' }}
          >
            {formatPct(totalAssetPct)}
          </span>
          <span
            className="bot-list-cell-secondary"
            style={{ color: totalAssetPositive ? '#00B061' : '#F0162F' }}
          >
            <HiddenValue>{signedNum(totalAssetAbs)}</HiddenValue>
          </span>
        </div>

        {/* Current profit */}
        <span
          className="bot-list-cell"
          style={{ color: currentProfitPositive ? '#00B061' : '#F0162F' }}
        >
          {formatPct(currentProfitPct)}
        </span>

        {/* Non-algo diff */}
        <span
          className="bot-list-cell"
          style={{ color: noAlgoPositive ? '#00B061' : '#F0162F' }}
        >
          {formatPct(noAlgoPct)}
        </span>

        {/* Quote/Asset bar */}
        <div className="bot-list-aq-cell">
          <QuoteAssetBar quoteRatio={quoteRatio} />
        </div>
      </div>

      {/* Expanded detail row (mobile) */}
      <div className="bot-list-detail">
        <div className="bot-list-detail-grid">
          <div className="bot-list-detail-item">
            <span className="bot-list-detail-label">Total Value</span>
            <span className="bot-list-detail-value">
              <HiddenValue width={55}>
                ${formatNumEU(sub.totalBalanceInStable)}
              </HiddenValue>
            </span>
          </div>
          <div className="bot-list-detail-item">
            <span className="bot-list-detail-label">Total Stable</span>
            <span
              className="bot-list-detail-value"
              style={{ color: totalStablePositive ? '#00B061' : '#F0162F' }}
            >
              {formatPct(totalStablePct)}
            </span>
          </div>
          <div className="bot-list-detail-item">
            <span className="bot-list-detail-label">Total Asset</span>
            <span
              className="bot-list-detail-value"
              style={{ color: totalAssetPositive ? '#00B061' : '#F0162F' }}
            >
              {formatPct(totalAssetPct)}
            </span>
          </div>
          <div className="bot-list-detail-item">
            <span className="bot-list-detail-label">Current</span>
            <span
              className="bot-list-detail-value"
              style={{ color: currentProfitPositive ? '#00B061' : '#F0162F' }}
            >
              {formatPct(currentProfitPct)}
            </span>
          </div>
          <div className="bot-list-detail-item">
            <span className="bot-list-detail-label">Non-Algo</span>
            <span
              className="bot-list-detail-value"
              style={{ color: noAlgoPositive ? '#00B061' : '#F0162F' }}
            >
              {formatPct(noAlgoPct)}
            </span>
          </div>
          <div className="bot-list-detail-item">
            <span className="bot-list-detail-label">Quote / Asset</span>
            <div style={{ width: '100%', maxWidth: 120 }}>
              <QuoteAssetBar quoteRatio={quoteRatio} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BotListRow;
